import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { createUseStyles } from 'react-jss'

import { stylesClasses, generateKey } from '../../utils/classes'
import { responsiveStyles } from '../../utils/styles'

import mediaQueries from '../../constants/mediaQueries'

const ROW_COLS = ['auto', 1, 2, 3, 4, 5, 6, 7, 8]
const ROW_JUSTIFY_CONTENT = ['start', 'center', 'end', 'around', 'between']
const ROW_ALIGN_ITEMS = ['start', 'center', 'end', 'stretch']

/**
 * Generate all the styles to be applied by JSS.
 * How-To use JSS styles:
 * <Component  customStyleProperty={key: value} />
 * [example]: <Modal width={sm: 100, md: 200, lg: 300} />
 */

/**
 * Returns object that stored, for each mediaQuery, desired key and its asociated CSS, for all sizes.
 * @param {string} mq - MediaQuery name.
 * @return {object} - All pairs {key: value}, where value is the CSS.
 */
function generateRowColsStyles(mq) {
	return ROW_COLS.reduce((styles, cols) => {
		const key = generateKey('row--cols-', cols, mq)

		if (cols === 'auto') {
			styles[key] = responsiveStyles(mq, {
				'& > *': { flex: '0 0 auto !important', width: 'auto' }
			})
		} else {
			const width = `${Math.round((1 / cols) * 10e7) / 10e5}%`
			styles[key] = responsiveStyles(mq, {
				'& > *': { flex: '0 0 auto !important', width }
			})
		}
		/**
		 * returned styles object example: {
		 *      row--cols-1: {'& > *':{flex: "0 0 auto", width: "8.333333%"}},
		 *      row--cols-md-auto: {@media screen and (min-width: 768px): {'& > *':{flex: "0 0 auto", width: "8.333333%"}}}
		 *      ...
		 *  }
		 */
		return styles
	}, {})
}

function generateRowJustifyContentStyles(mq) {
	return ROW_JUSTIFY_CONTENT.reduce((styles, justification) => {
		const key = generateKey('row--justify-', justification, mq)

		const attrValues = {
			start: 'flex-start',
			center: 'center',
			end: 'flex-end',
			around: 'space-around',
			between: 'space-between'
		}

		const value = attrValues[justification]

		if (value) {
			styles[key] = responsiveStyles(mq, { justifyContent: attrValues[justification] })
		}

		return styles
	}, {})
}

function generateRowAlignItemsStyles(mq) {
	return ROW_ALIGN_ITEMS.reduce((styles, alignment) => {
		const key = generateKey('row--align-', alignment, mq)

		const attrValues = {
			start: 'flex-start',
			center: 'center',
			end: 'flex-end',
			stretch: 'stretch',
		}

		const value = attrValues[alignment]

		if (value) {
			styles[key] = responsiveStyles(mq, { alignItems: attrValues[alignment] })
		}

		return styles
	}, {})
}

/**
 * Generate and merge all styles.
 */
const styles = {
	row: {
		'--bs-gutter-x': '1.5rem',
		'--bs-gutter-y': '0',
		display: 'flex',
		flexWrap: 'wrap',
		marginTop: 'calc(var(--bs-gutter-y) * -1)',
		'& > *': {
			flexShrink: 0,
			width: '100%',
			maxWidth: '100%',
			marginTop: 'var(--bs-gutter-y)'
		}
	},
	rowGutters: {
		marginRight: 'calc(var(--bs-gutter-x) / -2)',
		marginLeft: 'calc(var(--bs-gutter-x) / -2)',
		'& > *': {
			paddingRight: 'calc(var(--bs-gutter-x) / 2)',
			paddingLeft: 'calc(var(--bs-gutter-x) / 2)'
		}
	},
	...Object.keys(mediaQueries).reduce(
		(styles, mq) => ({
			...styles,
			...generateRowColsStyles(mq),
			...generateRowJustifyContentStyles(mq),
			...generateRowAlignItemsStyles(mq)
		}),
		{}
	)
}
/**
 * Then generate the JSS with createUseStyles, adding prefix "Vb".
 */
const useStyles = createUseStyles(styles, { name: 'Vb' })

const Row = forwardRef((props, ref) => {
	const {
		alignItems,
		className, // This prop is defined here to prevent its use. It's not allowed to override the column system.
		component: Component = 'div',
		justifyContent,
		disableGutters = false,
		rowCols,
		children,
		...other
	} = props

	const classes = useStyles()

	// If customStyleProps are setted, stylesClasses() matches the key
	// with the unique CSS selector generated by the JSS.
	return (
		<Component
			ref={ref}
			className={clsx(
				classes['row'],
				!disableGutters && classes['rowGutters'],
				rowCols && stylesClasses(rowCols, 'row--cols-', classes),
				justifyContent && stylesClasses(justifyContent, 'row--justify-', classes),
				alignItems && stylesClasses(alignItems, 'row--align-', classes)
			)}
			{...other}
		>
			{children}
		</Component>
	)
})

Row.displayName = 'Row'

export default Row

Row.defaultProps = {
	component: 'div',
	disableGutters: false
}

Row.propTypes = {
	/**
	 * Determine how cols are aligned.
	 */
	alignItems: PropTypes.oneOf(ROW_ALIGN_ITEMS),

	/**
	 * The content of the container.
	 */
	children: PropTypes.node,

	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * * @default 'div'
	 */
	component: PropTypes.elementType,

	/**
	 * If `true`, the left and right padding is removed.
	 * @default false
	 */
	disableGutters: PropTypes.bool,

	/**
	 * Determine how cols are justified.
	 */
	justifyContent: PropTypes.oneOf(ROW_JUSTIFY_CONTENT),

	/**
	 * Determine how many columns are per row.
	 */
	rowCols: (props, propName, componentName) => {
		const value = props[propName]
		if (typeof value === 'number') {
			return value >= 1 && value <= 6 ? null : new Error(propName + ' in ' + componentName + 'is not within 1 to 6')
		}

		return null
	}
}
